import React, { useEffect, useState } from 'react';
import { 
    Text, 
    IPanelStyles, 
    IPivotStyles, 
    Panel, 
    PanelType, 
    Pivot, 
    PivotItem, 
    Dropdown,
    IDropdownOption,
    DefaultButton,
} from '@fluentui/react';
import WorkOrderCreateComponent from '../../components/work-order/work-order-create.component';
import BreadcrumbComponent from '../../components/breadcrumb/breadcrumb.component';
import ButtonPrimaryComponent from '../../components/button-primary/button-primary.component';
import LoaderComponent from '../../components/loader/loader.component';
import DatePickerComponent from '../../components/date-picker/date-picker.component';
import ModalDefaultComponent from '../../components/modal-default/modal-default.component';
import ErrorMessageComponent from '../../components/error-message/error-message.component';
import NoPermissionComponent from '../../components/no-permission/no-permission.component';
import { useCookies } from "react-cookie";
import { useNavigate } from 'react-router-dom';
import WorkOrderReadonlyListComponent from '../../components/work-order/work-order-readonly-list.component';
import WorkReadonlyComponent from '../../components/work/work-readonly.component';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemFiltrosOrdenDeTrabajo from '../../models/ItemFiltrosOrdenDeTrabajo';
import ItemOrdenDeTrabajo from '../../models/ItemOrdenDeTrabajo';
import ItemOperario from '../../models/ItemOperario';
import ElementEnum from '../../enums/ElementEnum';
import ItemPermiso from '../../models/ItemPermiso';
import ItemBandejaEntrada from '../../models/ItemBandejaEntrada';
import ItemBandejaEntradaActuacion from '../../models/ItemBandejaEntradaActuacion';
import ItemTarea from '../../models/ItemTarea';
import ItemRegistroTiempo from '../../models/ItemRegistroTiempo';

const pivotStyles: IPivotStyles = {
    root: {},
    linkIsSelected: {
        selectors: {
            '::before': {
                backgroundColor: "var(--color-primario)",
            },
        },
    },
    link: {},
    linkContent: {},
    text: {},
    count: {},
    icon: {},
    linkInMenu: {},
    overflowMenuButton: {},
};

const panelFiltersStyles : IPanelStyles = {
    root: {},
    overlay: {},
    hiddenPanel: {},
    main: {},
    commands: {},
    contentInner: {},
    scrollableContent: {},
    navigation: {},
    header: {},
    headerText: {},
    content: {},
    footer: {},
    footerInner: {},
    subComponentStyles: {
        closeButton: {
            root: {
                color: 'rgb(50, 49, 48)',
            },
        },
    },
};

const stateOptions: IDropdownOption[] = [
    { 
        key: 0, 
        text: 'Todas',
    },
    { 
        key: 10, 
        text: 'Pendientes',
    },
    { 
        key: 20,
        text: 'En curso',
    },
    { 
        key: 30, 
        text: 'Finalizadas',
    },
    { 
        key: 40, 
        text: 'Cerradas',
    },
];

interface IWorkOrdersReadonlyPageProps {};

const WorkOrdersReadonlyPage = (props: IWorkOrdersReadonlyPageProps) => {
    const navigate = useNavigate();
    const auth = useAuthStatus();
    const workOrdersPermission : ItemPermiso = auth.getPermission(ElementEnum.WORK_ORDERS);

    const [cookies, setCookie] = useCookies(["work-orders-filters"]);

    const [workOrders, setWorkOrders] = useState<ItemBandejaEntrada[]>([]);
    const [workOrderOpen, setworkOrderOpen] = useState<boolean>(false);
    const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
    const [operators, setOperators] = useState<ItemOperario[]>([]);
    const [selectedOperatorIds, setSelectedOperatorIds] = useState<string[]>([]);
    const [filters, setFilters] = useState<ItemFiltrosOrdenDeTrabajo>(new ItemFiltrosOrdenDeTrabajo());
    const [selectedItem, setSelectedItem] = useState<ItemBandejaEntradaActuacion>();
    const [selectedWorkOrder, setSelectedWorkOrder] = useState<ItemBandejaEntrada>();
    const [selectedWork, setSelectedWork] = useState<ItemTarea>();
    const [selectedWorkParts, setSelectedWorkParts] = useState<ItemRegistroTiempo[]>();
    const [loading, setLoading] = useState<boolean>(true);
    const [submit, setSubmit] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (auth.user) {
            if (auth.user.ModoBienvenida) {
                navigate('/main/bienvenida/');
            }
            else if (workOrdersPermission.Ver) {
                if (auth.user.UsuarioAdmin) {
                    setLoading(true);
                    ApiService.Post<ItemOperario[]>("OrdenesTrabajo", "GetListaOperarios", auth.user).then((response: ItemOperario[]) => {
                        setOperators(response);
                        setLoading(false);
                    }, (error: string) => {
                        setError(error);
                        setLoading(false);
                    });
                }
                else {
                    let operator : ItemOperario = new ItemOperario();
                    operator.IdUsuario = auth.user.IdUsuario;
                    operator.Nombre = auth.user.Nombre;
                    setOperators([operator]);
                }
            }
        }
    }, [workOrdersPermission.Ver, auth.user, navigate]);

    useEffect(() => {
        setSelectedOperatorIds(operators.map((o: ItemOperario) => {
            return o.IdUsuario;
        }));
        setSubmit(true);
    }, [operators]);

    useEffect(() => {
        if (auth.user && workOrdersPermission.Ver && submit) {
            setLoading(true);
            setError('');
            setCookie("work-orders-filters", filters);
            sessionStorage.setItem('work-orders-filters', JSON.stringify(filters));
            setFiltersOpen(false);
            ApiService.Post<ItemBandejaEntrada[]>("OrdenesTrabajo", "GetBandejaEntradaExt", auth.user, filters).then((response: ItemBandejaEntrada[]) => {
                setWorkOrders(response);
                setSubmit(false);
                setLoading(false);
            }, (error: string) => {
                setError(error);
                setSubmit(false);
                setLoading(false);
            });
        }
    }, [auth.user, submit, filters, workOrdersPermission.Ver, setCookie]);

    const onStatusPivotChanged = (item?: PivotItem) : void => {
        if (item && item.props.itemKey) {
            setFilters({
                ...filters,
                Estado: parseInt(item.props.itemKey),
            });
            setSubmit(true);
        }
    };

    const onStatusDropdownChanged = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) : void => {
        if (option) {
            setFilters({
                ...filters,
                Estado: parseInt(option.key.toString()),
            });
        }
    };

    const onChangeBeginDate = (value: string) : void => {
        setFilters({
            ...filters,
            FechaDesde: value,
        });
    };

    const onChangeEndDate = (value: string) : void => {
        setFilters({
            ...filters,
            FechaHasta: value,
        });
    };

    const onChangeOperator = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) : void => {
        if (option) {
            setSelectedOperatorIds(option.selected 
                ? [...selectedOperatorIds, option.key as string] 
                : selectedOperatorIds.filter((id: string) => id !== option.key));
        }
    };

    useEffect(() => {
        setFilters((f: ItemFiltrosOrdenDeTrabajo) => {
            f.Operarios = selectedOperatorIds.join("|");
            return f;
        });
    }, [selectedOperatorIds]);

    const onFilter = () : void => {
        setSubmit(true);
    };

    const onResetFilter = () : void => {
        setFilters(new ItemFiltrosOrdenDeTrabajo());
        setSubmit(true);
    };

    const onSelectWork = (item: ItemBandejaEntradaActuacion, workOrder: ItemBandejaEntrada) : void => {
        if (auth.user) {
            setLoading(true);
            setSelectedItem(item);
            setSelectedWorkOrder(workOrder);
            ApiService.Post<ItemTarea>("OrdenesTrabajo", "GetTrabajo", auth.user, {
                "Id": item.IdTarea,
            }).then((response: ItemTarea) => {
                setSelectedWork(response);
                if (auth.user) {
                    ApiService.Post<ItemRegistroTiempo[]>("OrdenesTrabajo", "GetRegistroTiempos", auth.user, {
                        "IdOrden": workOrder.IdOrden,
                        "IdTarea": item.IdTarea,
                    }).then((response: ItemRegistroTiempo[]) => {
                        setSelectedWorkParts(response);
                        setLoading(false);
                    }, (error: string) => {
                        alert(error);
                        setLoading(false);
                    });
                }
            }, (error: string) => {
                alert(error);
                setLoading(false);
            });
        }
    };

    useEffect(() => {
        if (workOrders.length) {
            let workOrder : ItemBandejaEntrada | undefined = workOrders.find((workOrder: ItemBandejaEntrada) => {
                return (workOrder.Actuaciones.length > 0);
            });
            if (workOrder) {
                onSelectWork(workOrder.Actuaciones[0], workOrder);
            }
        }
    }, [workOrders]);

    const onWorkPartCreatedOrDeleted = () : void => {
        if (auth.user && selectedItem && selectedWorkOrder) {
            setLoading(true);
            ApiService.Post<ItemRegistroTiempo[]>("OrdenesTrabajo", "GetRegistroTiempos", auth.user, {
                "IdOrden": selectedWorkOrder.IdOrden,
                "IdTarea": selectedItem.IdTarea,
            }).then((response: ItemRegistroTiempo[]) => {
                setSelectedWorkParts(response);
                setLoading(false);
            }, (error: string) => {
                alert(error);
                setLoading(false);
            });
        }
    };

    const onWorkFinished = () : void => {
        if (auth.user && selectedItem && selectedWorkOrder) {
            onSelectWork(selectedItem, selectedWorkOrder);
        }
    };

    return (
        (auth.user && workOrdersPermission.Ver) ? (
            <div className='d-flex flex-column h-100 w-100'>
                <div className="row m-0">
                    <div className='col-12'>
                        <BreadcrumbComponent items={["Inicio", "Tareas"]} />
                    </div>
                </div>
                <div className="row m-0 justify-content-between">
                    <div className="col-auto">
                        {
                            workOrdersPermission.Nuevo ? (
                                <>
                                    <ButtonPrimaryComponent 
                                        text='Nueva tarea'
                                        className='d-none d-md-inline-block'
                                        icon='AddTo'
                                        onClick={() => setworkOrderOpen(true)} />
                                    <ButtonPrimaryComponent 
                                        className='d-inline-block d-md-none'
                                        icon='AddTo'
                                        onClick={() => setworkOrderOpen(true)} />
                                </>
                            ) : (
                                null
                            )
                        }
                    </div>
                    <div className='col-auto'>
                        <ButtonPrimaryComponent 
                            text='Filtros'
                            className='d-none d-md-inline-block'
                            icon='Filter'
                            secondary={true}
                            onClick={() => setFiltersOpen(true)} />
                        <ButtonPrimaryComponent 
                            className='d-inline-block d-md-none'
                            icon='Filter'
                            secondary={true}
                            onClick={() => setFiltersOpen(true)} />
                        <ButtonPrimaryComponent 
                            text='Actualizar'
                            className='d-none d-md-inline-block'
                            icon='Refresh'
                            onClick={() => setSubmit(true)} />
                        <ButtonPrimaryComponent 
                            className='d-inline-block d-md-none'
                            icon='Refresh'
                            onClick={() => setSubmit(true)} />
                    </div>
                </div>
                <div className="row m-0 align-items-center">
                    <div className="col-12 py-3">
                        <Pivot
                            styles={pivotStyles}
                            selectedKey={filters.Estado.toString()}
                            onLinkClick={onStatusPivotChanged}
                            headersOnly={true}>
                            <PivotItem 
                                itemKey="0" 
                                headerText='Todas' />
                            <PivotItem 
                                itemKey="10" 
                                headerText='Pendientes' />
                            <PivotItem 
                                itemKey="20" 
                                headerText='En curso' />
                            <PivotItem 
                                itemKey="30" 
                                headerText='Finalizadas' />
                            <PivotItem 
                                itemKey="40" 
                                headerText='Cerradas' />
                        </Pivot>
                    </div>
                </div>
                <div className="row m-0 flex-fill overflow-hidden h-100">
                    <div className='col-12 col-md-4 mh-100 overflow-auto'>
                        <WorkOrderReadonlyListComponent
                            onSelectWork={onSelectWork}
                            workOrders={workOrders}
                            selectedWorkOrder={selectedWorkOrder}
                            selectedWork={selectedItem} />
                    </div>
                    <div className='col-12 col-md-8 mh-100 overflow-auto d-none d-md-block'>
                        {
                            (selectedItem && selectedWork && selectedWorkParts) ? (
                                <WorkReadonlyComponent
                                    onWorkPartCreatedOrEdited={onWorkPartCreatedOrDeleted}
                                    onWorkPartDeleted={onWorkPartCreatedOrDeleted}
                                    onWorkFinished={onWorkFinished}
                                    item={selectedItem}
                                    work={selectedWork}
                                    workParts={selectedWorkParts}
                                    setLoading={setLoading} />
                            ) : (
                                null
                            )
                        }
                    </div>
                    {
                        (selectedItem && selectedWork && selectedWorkParts) ? (
                            <ModalDefaultComponent
                                title="Tarea"
                                className='d-flex d-md-none'
                                onDismiss={() => setSelectedItem(undefined)}>
                                <WorkReadonlyComponent
                                    onWorkPartCreatedOrEdited={onWorkPartCreatedOrDeleted}
                                    onWorkPartDeleted={onWorkPartCreatedOrDeleted}
                                    onWorkFinished={onWorkFinished}
                                    item={selectedItem}
                                    work={selectedWork}
                                    workParts={selectedWorkParts}
                                    setLoading={setLoading} />
                            </ModalDefaultComponent>
                        ) : (
                            null
                        )
                    }
                </div>
                <Panel 
                    isOpen={filtersOpen} 
                    onDismiss={() => setFiltersOpen(false)} 
                    type={PanelType.smallFixedFar}
                    headerText="Filtros"
                    styles={panelFiltersStyles}>
                    <div className="container-fluid p-0">
                        <div className='row m-0'>
                            <div className='col-12 p-0'>
                                <Text className='d-block my-2'>
                                    Haz una búsqueda más rápida y avanzada
                                </Text>
                            </div>
                            <div className='col-12 p-0'>
                                <Text className='d-block my-2'>
                                    Estado
                                </Text>
                                <Dropdown
                                    selectedKey={filters.Estado}
                                    onChange={onStatusDropdownChanged}
                                    options={stateOptions} />
                            </div>
                            <div className='col-12 p-0'>
                                <Text className='d-block my-2'>
                                    Intervalo de fechas
                                </Text>
                                <DatePickerComponent 
                                    value={filters.FechaDesde}
                                    onChange={onChangeBeginDate} />
                                <DatePickerComponent 
                                    value={filters.FechaHasta}
                                    onChange={onChangeEndDate} />
                            </div>
                            <div className='col-12 p-0'>
                                <Text className='d-block my-2'>
                                    Operarios
                                </Text>
                                <Dropdown
                                    defaultSelectedKeys={selectedOperatorIds}
                                    multiSelect
                                    onChange={onChangeOperator}
                                    options={operators.map((o: ItemOperario) => {
                                        return {
                                            key: o.IdUsuario,
                                            text: o.Nombre,
                                        };
                                    })}>
                                </Dropdown>
                            </div>
                        </div>
                        <div className='row mx-0 my-3 justify-content-between'>
                            <div className='col-auto p-0'>
                                <DefaultButton 
                                    type='submit'
                                    text='Aplicar filtros'
                                    onClick={onFilter} />
                            </div>
                            <div className='col-auto p-0'>
                                <DefaultButton 
                                    type='button'
                                    text='Restablecer filtros'
                                    onClick={onResetFilter} />
                            </div>
                        </div>
                    </div>
                </Panel>
                {
                    workOrderOpen ? (
                        <WorkOrderCreateComponent
                            isOpen={true}
                            onWorkOrderCreated={() => setFilters({...filters})}
                            onClose={() => setworkOrderOpen(false)} />
                    ) : (
                        null
                    )
                }
                <ErrorMessageComponent message={error} />
                <LoaderComponent loading={loading} />
            </div>
        ) : (
            <NoPermissionComponent showButton={true} />
        )
    );
};

export default WorkOrdersReadonlyPage;
